import {
  ChannelType,
  Dimension,
  DimensionType,
  EulaType,
  OfferStatus,
  Price,
  PriceTypes,
  ScheduleType,
} from '../privateOffer/reducer'
import { InstallmentInfo } from '../../../externalApps/modules/cppoResellerAgreement/reducer'
import { CombineFormActionTypes } from './action'
import { Reducer } from 'redux'
import {
  ContractDurationType,
  DiscountType,
} from '../../../admin/modules/resaleAuthorizations/reducer'

export type CloudInfo = {
  resellerAwsAccountId?: string
  labraPlatformResellerId?: string
}
export type CloudInfoCppo = {
  awsAccountId?: string
}
export interface CombinedFormDimension {
  id?: string
  dimensionId?: string
  type: DimensionType
  price?: Price
  prices?: PriceTypes
  currency: string
  quantity?: number
  label?: string
  labelDescription?: string
  name?: string
  productName?: string
  awsProductId?: string
  crmObjectId?: string
  isAdditionalUsageAllowed?: boolean
  createdAt?: string
  additionalUsageDescription?: string
}

export interface ResaleAuditJson {
  resaleAuthCreationFailedAt?: string
}
export interface OfferAuditJson {
  offerCreatedAt: string | null
  offerSentAt: string | null
  offerViewedAt: string | null
  offerAcceptedAt: string | null
  offerCancelledAt: string | null
  offerCreatedInLabraAt: string | null
  offerFailedAt?: string
}
export interface ResellerAgreementData {
  agreementErrorMessage?: string
  tcv?: number | null
  agreementDescription: string
  agreementName: string
  companyName: string
  buyerAwsAccountId: string
  endCustomerEulaType: EulaType
  currency: string
  cloudProductUid: string
  productType: string
  dimensions: CombinedFormDimension[]
  resellersInfo: CloudInfo[]
  renewal: boolean
  renewalChannel: ChannelType | null
  contractDurationInMonthsForEndCustomer: number | null
  awsProductId?: string
  renewalPricingModel?: string
  installmentInfo: InstallmentInfo[]
  flexiblePaymentType?: ScheduleType
  firstInstallmentDate: string | null
  draft?: boolean
  agreementExpirationDate?: string | null
  contractDurationTimeUnit?: string
  contractDurationType?: ContractDurationType
  discountType?: string
  status?: string
  agreementStatus?: string
  isvName?: string
  agreementAuditJson?: ResaleAuditJson
}

export interface CppoDimension {
  id?: string
  label: string
  labelDescription: string
  name: string
  type: string
  isAdditionalUsageAllowed: boolean
  dimensionUnit: string
  awsProductId: string
  dimensionId: string
  currency: string
  price1m: number
  price12m: number
  price24m: number
  price36m: number
  partnerMargin: number
  resellerAgreementDimensionId: string
  additionalUsagePrice?: number
  price?: number
}

export interface CppoData {
  offerErrorMessage?: string
  tcv?: number | null
  offerName: string
  offerDescription: string
  sellerId: string
  companyName: string
  buyerCloudAccountId: string
  flexiblePaymentTerms: boolean
  flexiblePaymentType?: ScheduleType
  serviceLength: number | null
  eulaType: EulaType
  offerExpirationDate: string | null
  isvName: string
  priceAdjustmentType: string
  adjustmentPercentage: number | string
  awsProductId: string
  installmentInfo: InstallmentInfo[]
  dimensions: CppoDimension[]
  channelPartnerName: string
  agreementStartDate: string | null
  subscriptionEndDate: string | null
  variableStartDate: boolean
  eulaFile?: string[]
  adjustmentPercentageInputValue?: number | string
  firstInstallmentDate: string
  currency: string
  draft?: boolean
  cpInfo: CloudInfoCppo
  offerStatus?: OfferStatus
  cloudOfferUrl?: string
  cloudOfferId?: string
  offerAuditJson?: OfferAuditJson
}

export interface CombinedFormAuditLogs {
  ACCEPTED?: Array<{ at: string; by?: string }>
  REJECTED?: Array<{ at: string; by?: string }>
  DRAFT?: Array<{ at: string; by?: string }>
}

export interface CombinedFormState {
  id: string | null
  labraIsvId: string
  labraCpId: string
  resellerAgreementData: ResellerAgreementData
  cppoData: CppoData
  partnerAccountId?: string
  labraResellerAgreementId?: string
  labraCppoId?: string
  status?: string
  externalId?: string
  auditLogs?: CombinedFormAuditLogs
}

export const initialState: CombinedFormState = {
  id: null,
  labraIsvId: '',
  labraCpId: '',
  resellerAgreementData: {
    agreementStatus: 'completed',
    agreementDescription: '-',
    agreementName: '',
    companyName: '',
    buyerAwsAccountId: '',
    endCustomerEulaType: 'STANDARD' as EulaType,
    currency: 'USD',
    cloudProductUid: '',
    productType: '',
    awsProductId: '',
    renewalPricingModel: '',
    dimensions: [],
    resellersInfo: [
      {
        resellerAwsAccountId: '',
      },
    ],
    renewal: false,
    renewalChannel: null,
    contractDurationInMonthsForEndCustomer: null,
    installmentInfo: [],
    firstInstallmentDate: null,
    agreementExpirationDate: null,
    draft: false,
    tcv: null,
    contractDurationTimeUnit: 'MONTHS',
    contractDurationType: 'custom' as ContractDurationType,
    discountType: 'flexible_payment_terms',
    isvName: '',
  },

  cppoData: {
    cloudOfferUrl: 'https://google.com',
    cloudOfferId: 'offer-123123',
    firstInstallmentDate: '',
    flexiblePaymentType: 'CUSTOM' as ScheduleType,
    offerName: '',
    offerDescription: '-',
    sellerId: '',
    companyName: '',
    buyerCloudAccountId: '',
    flexiblePaymentTerms: true,
    currency: 'USD',
    serviceLength: null,
    eulaType: 'STANDARD' as EulaType,
    offerExpirationDate: null,
    isvName: '',
    priceAdjustmentType: 'markup',
    adjustmentPercentage: '',
    adjustmentPercentageInputValue: '',
    awsProductId: '',
    installmentInfo: [],
    dimensions: [],
    channelPartnerName: '',
    agreementStartDate: null,
    subscriptionEndDate: null,
    variableStartDate: false,
    eulaFile: [],
    draft: false,
    tcv: null,
    cpInfo: {
      awsAccountId: '',
    },
  },
}

type ActionType =
  | {
      type: CombineFormActionTypes.SET_COMBINED_FORM_DATA
      payload: CombinedFormState
    }
  | {
      type: CombineFormActionTypes.CLEAR_COMBINED_FORM_DATA
    }

export const reducer: Reducer<CombinedFormState, ActionType> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case CombineFormActionTypes.SET_COMBINED_FORM_DATA: {
      return {
        ...state,
        ...action.payload, // merge updated data
      }
    }

    case CombineFormActionTypes.CLEAR_COMBINED_FORM_DATA: {
      return {
        ...initialState, // reset to initial values
      }
    }

    default:
      return state
  }
}
